import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../config';

/**
 * MidFlightCheck - Burnout Detection Modal
 * 
 * Triggered when:
 * - User misses 2 consecutive days
 * - User marks task as "Failed"
 * 
 * Options:
 * - Keep going (had a busy week)
 * - Adjust Intensity (make task easier)
 * - Pause Journey (try again later)
 */

const MidFlightCheck = ({ journey, onClose, onAdjust }) => {
  const [selected, setSelected] = useState(null);
  const [newHabit, setNewHabit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAdjustInput, setShowAdjustInput] = useState(false);

  const missedDays = journey?.consecutive_missed_days || 2;
  const currentHabit = journey?.tiny_win || journey?.target_habit || 'your habit';

  const handleSelect = (option) => {
    setSelected(option);
    if (option === 'adjust') {
      setShowAdjustInput(true);
    } else {
      setShowAdjustInput(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (selected === 'continue') {
        // Just close the modal, reset the missed counter
        await axios.post(`${API}/journey/acknowledge-missed`);
        onClose();
      } else if (selected === 'adjust') {
        // Update the habit to easier version
        await axios.post(`${API}/journey/adjust`, {
          action: 'adjust',
          new_habit: newHabit || currentHabit
        });
        onAdjust && onAdjust('adjusted');
        onClose();
      } else if (selected === 'pause') {
        // Pause the journey
        await axios.post(`${API}/journey/pause`);
        onAdjust && onAdjust('paused');
        onClose();
      }
    } catch (err) {
      console.error('Failed to submit mid-flight decision:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="text-5xl">💙</span>
          <h2 className="text-2xl font-bold text-gray-900 mt-4">Hey, No Guilt Here</h2>
          <p className="text-gray-600 mt-2">
            I noticed you missed {missedDays} day{missedDays > 1 ? 's' : ''} in a row. Life happens.
          </p>
        </div>

        {/* Compassionate Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-blue-800 text-sm">
            Ideally, we want to keep the streak going, but maybe the pace is too fast? 
            Let's check in and see what feels right.
          </p>
        </div>

        {/* Current Habit Reminder */}
        <div className="bg-gray-50 rounded-xl p-3 mb-6">
          <p className="text-gray-600 text-sm">
            <strong>Current habit:</strong> {currentHabit}
          </p>
        </div>

        {/* Options */}
        <div className="space-y-3 mb-6">
          <button
            onClick={() => handleSelect('continue')}
            className={`w-full p-4 rounded-xl border-2 transition text-left ${
              selected === 'continue'
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-emerald-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">💪</span>
              <div>
                <div className="font-semibold text-gray-900">Keep Going</div>
                <div className="text-sm text-gray-500">I just had a busy week, I'm back on track</div>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleSelect('adjust')}
            className={`w-full p-4 rounded-xl border-2 transition text-left ${
              selected === 'adjust'
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-200 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎯</span>
              <div>
                <div className="font-semibold text-gray-900">Adjust Intensity</div>
                <div className="text-sm text-gray-500">Make the habit smaller/easier</div>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleSelect('pause')}
            className={`w-full p-4 rounded-xl border-2 transition text-left ${
              selected === 'pause'
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 hover:border-purple-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">⏸️</span>
              <div>
                <div className="font-semibold text-gray-900">Pause Journey</div>
                <div className="text-sm text-gray-500">Take a break, try again when ready</div>
              </div>
            </div>
          </button>
        </div>

        {/* Adjust Input */}
        {showAdjustInput && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What's an easier version of this habit?
            </label>
            <textarea
              value={newHabit}
              onChange={(e) => setNewHabit(e.target.value)}
              placeholder={`e.g., Instead of "${currentHabit}", try something smaller...`}
              className="w-full border-2 border-gray-200 focus:border-amber-500 rounded-xl p-3 outline-none resize-none h-20"
            />
            <p className="text-gray-500 text-xs mt-1">
              💡 Tip: What could you do in under 2 minutes, even on your worst day?
            </p>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!selected || submitting || (selected === 'adjust' && !newHabit.trim())}
          className={`w-full py-3 rounded-xl font-semibold transition ${
            selected && !submitting && (selected !== 'adjust' || newHabit.trim())
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {submitting ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default MidFlightCheck;
